import { useEffect, useState } from "react";
import { useGetList } from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { graduationRequests } from "api/graduationApi";
import { studentRequests } from "api/studentApi";
import { ALL_OPTIONS } from "./constants";

/**
 * Draft + options of the internship pre-filter (state exams, thesis defense, training bases and - with `withPlan` -
 * the internship schedules), for a list to pass straight to <CustomFilter>:
 *
 *   const internship = useInternshipFilter({ filters, withPlan: true });
 *   <CustomFilter filters={internship.fields} buttonDisabled={!internship.isComplete}
 *     buttonOnClick={() => setFilters(internship.query)} />
 *
 * `filters` is the list's committed useStore object - it seeds the draft. Cascade: year + structural unit -> session
 * (-> plan). `sessionRequired` - whether a session must be picked before searching. The first year is preselected.
 * Returns `fields` (CustomFilter's filter definitions), `isComplete` and `query` (the draft without empty values).
 */
const useInternshipFilter = ({ filters, withPlan = false, sessionRequired = true }) => {
  // `undefined` = not chosen yet, so the first year gets preselected; `null` = "Всички" (cleared on purpose, or a
  // committed search without a year) and stays empty.
  const [academicYearId, setAcademicYearId] = useState(filters ? (filters.academicYearId ?? null) : undefined);
  const [structuralUnitId, setStructuralUnitId] = useState(filters?.structuralUnitId ?? null);
  const [internshipSessionId, setInternshipSessionId] = useState(filters?.internshipSessionId ?? null);
  const [planId, setPlanId] = useState(filters?.planId ?? null);

  const { data: academicYears } = useGetList(studentRequests.getAcademicYearsSelect(), {
    ...ALL_OPTIONS,
    sort: { field: "name", order: "ASC" },
  });
  const { data: structuralUnits } = useGetList(administrationRequests.getStructuralUnitsSelect(), {
    ...ALL_OPTIONS,
    filter: { filterByUser: true },
  });
  const { data: sessions } = useGetList(
    graduationRequests.getInternshipSessionsSelect(),
    { ...ALL_OPTIONS, filter: { structuralUnitId, academicYearId } },
    { enabled: Boolean(structuralUnitId && academicYearId) },
  );
  const { data: plans } = useGetList(
    graduationRequests.getInternshipPlansSelect(),
    { ...ALL_OPTIONS, filter: { structuralUnitId } },
    { enabled: Boolean(withPlan && structuralUnitId && internshipSessionId) },
  );

  useEffect(() => {
    if (academicYearId === undefined && academicYears?.length) setAcademicYearId(academicYears[0].id);
  }, [academicYearId, academicYears]);

  // Sessions read "type - year", like InternshipSessionInput.
  const yearName = id => academicYears?.find(year => year.id === id)?.name ?? "";
  const sessionChoices = (sessions ?? []).map(session => ({
    id: session.id,
    name: `${session.typeText} - ${yearName(session.academicYearId)}`,
  }));

  // Each level clears the ones below it.
  const changeYear = value => {
    setAcademicYearId(value || null);
    setStructuralUnitId(null);
    setInternshipSessionId(null);
    setPlanId(null);
  };
  const changeStructuralUnit = value => {
    setStructuralUnitId(value);
    setInternshipSessionId(null);
    setPlanId(null);
  };
  const changeSession = value => {
    setInternshipSessionId(value);
    setPlanId(null);
  };

  const fields = [
    {
      label: "Учебна година",
      type: "select",
      value: academicYearId ?? "",
      setValue: changeYear,
      choices: academicYears ?? [],
      emptyText: "Всички",
    },
    {
      label: "Структурна единица",
      type: "select",
      value: structuralUnitId ?? "",
      setValue: changeStructuralUnit,
      choices: structuralUnits ?? [],
    },
    ...(structuralUnitId && academicYearId
      ? [
          {
            label: "Стажантска сесия",
            type: "select",
            value: internshipSessionId ?? "",
            setValue: changeSession,
            choices: sessionChoices,
          },
        ]
      : []),
    ...(withPlan && structuralUnitId && internshipSessionId
      ? [{ label: "Стажантски план", type: "autocomplete", value: planId, setValue: setPlanId, choices: plans ?? [] }]
      : []),
  ];

  const isComplete = Boolean(
    structuralUnitId && (!sessionRequired || internshipSessionId) && (!withPlan || (internshipSessionId && planId)),
  );

  const draft = { academicYearId, structuralUnitId, internshipSessionId, ...(withPlan && { planId }) };
  const query = Object.fromEntries(Object.entries(draft).filter(([, value]) => value != null && value !== ""));

  return { fields, isComplete, query };
};

export default useInternshipFilter;
